import React from 'react'
import Form from 'react-bootstrap/Form'
import { Row, Col } from 'react-bootstrap'

import SectionHeader from '@components/SectionHeader'
import { ProgramEventDetail, EventDetailQuestion } from '@interfaces/events'

const { I18n } = window

interface EventDetailQuestionsSectionProps {
  questions: EventDetailQuestion[]
  existingDetails: ProgramEventDetail[]
}

const isImperialLocale = (): boolean => {
  const locale = (I18n.locale || '').toLowerCase().replace(/-/g, '_')
  return locale === 'en' || locale === 'en_us'
}

const findExistingDetail = (
  existingDetails: ProgramEventDetail[],
  questionId: number
): ProgramEventDetail | undefined =>
  existingDetails.find(
    (detail) => Number(detail.event_detail_question_id) === Number(questionId)
  )

const toDateInputValue = (value: string | null | undefined): string => {
  if (!value) return ''

  const mdy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (mdy) {
    const [, month, day, year] = mdy
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }

  return value
}

const firstError = (
  errors: Record<string, string[]> | undefined,
  fields: string[]
): string | undefined => {
  if (!errors) return undefined
  for (let i = 0; i < fields.length; i++) {
    const msgs = errors[fields[i]]
    if (msgs && msgs.length > 0) return msgs[0]
  }
  return undefined
}

const FieldErrorFeedback: React.FC<{ message: string | undefined }> = ({ message }) => {
  if (!message) return null
  return (
    <Form.Control.Feedback type="invalid" className="d-block">
      {message}
    </Form.Control.Feedback>
  )
}

const QuestionHint: React.FC<{ description: string | null }> = ({ description }) => {
  if (!description) return null

  return <Form.Text className="text-muted" dangerouslySetInnerHTML={{ __html: description }} />
}

const EventDetailQuestionsSection: React.FC<EventDetailQuestionsSectionProps> = ({
  questions,
  existingDetails,
}) => {
  if (!questions || questions.length === 0) return null

  const imperial = isImperialLocale()

  const hasDetailErrors = existingDetails.some(function (d) {
    return d.errors && Object.keys(d.errors).length > 0
  })

  return (
    <fieldset className={hasDetailErrors ? 'was-validated' : undefined}>
      <SectionHeader text={I18n.t('views.events.event_detail_questions')} />

      {questions.map((question, index) => {
        const fieldName = `program_event[program_event_details_attributes][${index}]`
        const existing = findExistingDetail(existingDetails, question.id)
        const defaultValue = question.default_value || ''
        const value = existing && existing.value != null ? existing.value : defaultValue
        const questionErrors = existing && existing.errors ? existing.errors : undefined

        return (
          <Row key={question.id} className="pt-2">
            <input type="hidden" name={`${fieldName}[input_type]`} value={question.input_type} />
            <input
              type="hidden"
              name={`${fieldName}[event_detail_question_id]`}
              value={question.id}
            />
            {existing && existing.id ? (
              <input type="hidden" name={`${fieldName}[id]`} value={existing.id} />
            ) : null}

            {question.input_type === 'text' && (
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>
                    {question.question}
                    {question.required ? ' *' : ''}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name={`${fieldName}[value]`}
                    defaultValue={value}
                    required={question.required}
                    isInvalid={!!firstError(questionErrors, ['value'])}
                  />
                  <FieldErrorFeedback message={firstError(questionErrors, ['value'])} />
                  <QuestionHint description={question.question_description} />
                </Form.Group>
              </Col>
            )}

            {question.input_type === 'number' && (
              <Col lg={6} xs={12}>
                <Form.Group>
                  <Form.Label>
                    {question.question}
                    {question.required ? ' *' : ''}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name={`${fieldName}[value]`}
                    defaultValue={value}
                    required={question.required}
                    isInvalid={!!firstError(questionErrors, ['value'])}
                  />
                  <FieldErrorFeedback message={firstError(questionErrors, ['value'])} />
                  <QuestionHint description={question.question_description} />
                </Form.Group>
              </Col>
            )}

            {question.input_type === 'date' && (
              <Col lg={6} xs={12}>
                <Form.Group>
                  <Form.Label>
                    {question.question}
                    {question.required ? ' *' : ''}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name={`${fieldName}[value]`}
                    defaultValue={toDateInputValue(value)}
                    required={question.required}
                    isInvalid={!!firstError(questionErrors, ['value'])}
                  />
                  <FieldErrorFeedback message={firstError(questionErrors, ['value'])} />
                  <QuestionHint description={question.question_description} />
                </Form.Group>
              </Col>
            )}

            {question.input_type === 'dropdown' && (
              <Col xs={question.question.length > 100 ? 12 : 12} lg={question.question.length > 100 ? 12 : 6}>
                <Form.Group>
                  <Form.Label>
                    {question.question}
                    {question.required ? ' *' : ''}
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name={`${fieldName}[value]`}
                    defaultValue={value}
                    required={question.required}
                    isInvalid={!!firstError(questionErrors, ['value'])}
                  >
                    <option value=""></option>
                    {(question.question_responses || []).map((response) => (
                      <option key={response.id} value={response.value}>
                        {response.value}
                      </option>
                    ))}
                  </Form.Control>
                  <FieldErrorFeedback message={firstError(questionErrors, ['value'])} />
                  <QuestionHint description={question.question_description} />
                </Form.Group>
              </Col>
            )}

            {question.input_type === 'radio_buttons' && (
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>
                    {question.question}
                    {question.required ? ' *' : ''}
                  </Form.Label>
                  {(question.question_responses || []).map((response) => (
                    <Form.Check
                      key={response.id}
                      type="radio"
                      name={`${fieldName}[value]`}
                      id={`${fieldName}_value_${response.id}`}
                      value={response.value}
                      label={response.value}
                      defaultChecked={value === response.value}
                      required={question.required}
                    />
                  ))}
                  <FieldErrorFeedback message={firstError(questionErrors, ['value'])} />
                  <QuestionHint description={question.question_description} />
                </Form.Group>
              </Col>
            )}

            {question.input_type === 'checkboxes' && (
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>
                    {question.question}
                    {question.required ? ' *' : ''}
                  </Form.Label>
                  {(question.question_responses || []).map((response) => {
                    const checkedValues = value ? value.split(';;') : []
                    const isChecked =
                      checkedValues.includes(response.value) ||
                      (!value && defaultValue === response.value)

                    return (
                      <Form.Check
                        key={response.id}
                        type="checkbox"
                        name={`${fieldName}[value][]`}
                        id={`${fieldName}_value_${response.id}`}
                        value={response.value}
                        label={response.value}
                        defaultChecked={isChecked}
                      />
                    )
                  })}
                  <FieldErrorFeedback message={firstError(questionErrors, ['value'])} />
                  <QuestionHint description={question.question_description} />
                </Form.Group>
              </Col>
            )}

            {question.input_type === 'height' && imperial && (
              <>
                <Col xs={6}>
                  <Form.Group>
                    <Form.Label>
                      {I18n.t('views.detail_question.height_feet_label', {
                        question: question.question,
                      })}
                      {question.required ? ' *' : ''}
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name={`${fieldName}[height_feet]`}
                      defaultValue={existing && existing.height_feet != null ? existing.height_feet : ''}
                      required={question.required}
                      isInvalid={!!firstError(questionErrors, ['height_feet', 'height_inches'])}
                    />
                    <FieldErrorFeedback message={firstError(questionErrors, ['height_feet', 'height_inches'])} />
                    <QuestionHint description={question.question_description} />
                  </Form.Group>
                </Col>
                <Col xs={6}>
                  <Form.Group>
                    <Form.Label>
                      {I18n.t('views.detail_question.height_inches_label', {
                        question: question.question,
                      })}
                      {question.required ? ' *' : ''}
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name={`${fieldName}[height_inches]`}
                      defaultValue={existing && existing.height_inches != null ? existing.height_inches : ''}
                      required={question.required}
                      isInvalid={!!firstError(questionErrors, ['height_inches', 'height_feet'])}
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            {question.input_type === 'height' && !imperial && (
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>
                    {I18n.t('views.detail_question.height_centimeters_label', {
                      question: question.question,
                    })}
                    {question.required ? ' *' : ''}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name={`${fieldName}[height_cm]`}
                    defaultValue={existing && existing.height_cm != null ? existing.height_cm : ''}
                    required={question.required}
                    isInvalid={!!firstError(questionErrors, ['height_cm'])}
                  />
                  <FieldErrorFeedback message={firstError(questionErrors, ['height_cm'])} />
                  <QuestionHint description={question.question_description} />
                </Form.Group>
              </Col>
            )}

            {question.input_type === 'weight' && imperial && (
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>
                    {I18n.t('views.detail_question.weight_pounds_label', {
                      question: question.question,
                    })}
                    {question.required ? ' *' : ''}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name={`${fieldName}[value]`}
                    defaultValue={existing && existing.value != null ? existing.value : value}
                    required={question.required}
                    isInvalid={!!firstError(questionErrors, ['value'])}
                  />
                  <FieldErrorFeedback message={firstError(questionErrors, ['value'])} />
                  <QuestionHint description={question.question_description} />
                </Form.Group>
              </Col>
            )}

            {question.input_type === 'weight' && !imperial && (
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>
                    {I18n.t('views.detail_question.weight_kilograms_label', {
                      question: question.question,
                    })}
                    {question.required ? ' *' : ''}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name={`${fieldName}[weight_kg]`}
                    defaultValue={existing && existing.weight_kg != null ? existing.weight_kg : ''}
                    required={question.required}
                    isInvalid={!!firstError(questionErrors, ['weight_kg'])}
                  />
                  <FieldErrorFeedback message={firstError(questionErrors, ['weight_kg'])} />
                  <QuestionHint description={question.question_description} />
                </Form.Group>
              </Col>
            )}
          </Row>
        )
      })}
    </fieldset>
  )
}

export default EventDetailQuestionsSection
